/**
 * Il proprio profilo: cosa si può cambiare da soli e cosa no.
 *
 * Da qui si cambiano **la password e la ricezione degli avvisi**. Nome, email,
 * ruolo e ultimo accesso si leggono soltanto — il perché è scritto nella
 * pagina, in chiaro, perché è una regola che ha una ragione e non un permesso
 * dimenticato.
 *
 * ⚠️ Il cambio password chiude tutte le sessioni, **compresa questa**. È il
 * comportamento voluto del backend, ma è anche l'unica azione
 * dell'applicazione che porta via chi la esegue: va detta prima di premere,
 * confermata dopo, e spiegata anche all'arrivo sulla schermata di accesso
 * (vedi `auth/congedo.ts`).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ErroreApi } from '../api/client.ts';
import { autenticazione } from '../api/scadenziario.ts';
import { annunciaCongedo } from '../auth/congedo.ts';
import { useAutenticazione } from '../auth/contesto.tsx';
import { Icona } from '../componenti/Icona.tsx';
import { InterruttoreNotifiche } from '../componenti/InterruttoreNotifiche.tsx';
import { istante } from '../componenti/formato.ts';
import { ETICHETTE_RUOLO, SPIEGAZIONE_RUOLO, puoAmministrare } from '../auth/ruoli.ts';

export function Profilo() {
  const { utente, ricarica } = useAutenticazione();

  // Il contesto qui non è mai `null` — questa rotta si raggiunge solo da
  // dentro l'applicazione — ma TypeScript non lo sa e non deve saperlo.
  if (utente === null) return null;

  const sonoAmministratore = puoAmministrare(utente.ruolo);

  return (
    <>
      {/* Titolo e pannelli condividono la stessa larghezza e lo stesso
          centraggio: se il titolo restasse ancorato a sinistra mentre i
          pannelli si centrano, la pagina sembrerebbe montata storta. */}
      <div className="titolo-pagina larghezza-profilo"><h1>Il tuo profilo</h1></div>

      <div className="colonne-profilo">
        <div>
          <section className="pannello pannello-imbottito" aria-labelledby="profilo-dati">
            <h2 className="titolo-riquadro" id="profilo-dati">I tuoi dati</h2>

            <dl className="scheda-dati">
              <dt>Nome</dt>
              <dd>{utente.nome}</dd>

              <dt>Email</dt>
              <dd>{utente.email}</dd>

              <dt>Ruolo</dt>
              <dd>
                {ETICHETTE_RUOLO[utente.ruolo]}
                <span className="dato-nota">{SPIEGAZIONE_RUOLO[utente.ruolo]}</span>
              </dd>

              <dt>Ultimo accesso</dt>
              <dd>
                {utente.ultimoAccesso === null
                  ? <span className="tenue">nessuno registrato prima di questo</span>
                  : istante(utente.ultimoAccesso)}
                <span className="dato-nota">
                  È l'accesso precedente a quello in corso. Se non lo riconosci, cambia
                  subito la password qui accanto.
                </span>
              </dd>
            </dl>

            {/*
              Il perché, e non solo il divieto.
              «Rivolgiti a un amministratore» senza il motivo si legge come
              burocrazia, e la burocrazia si aggira: prima o poi qualcuno
              chiederebbe di togliere il vincolo, e nessuno saprebbe più dire
              cosa si stava proteggendo.
            */}
            <div className="nota-regola">
              <p>
                <strong>Nome ed email non si cambiano da qui.</strong> Sono il nome con cui
                l'applicazione firma quello che fai: ogni scadenza che completi, annulli o
                riapri resta scritta col tuo nome, e quella riga va letta anche fra due anni.
                Se ciascuno potesse riscriversi il proprio, «chi ha completato questa
                scadenza» diventerebbe una risposta che può cambiare chi l'ha data.
              </p>
              <p>
                {sonoAmministratore ? (
                  <>
                    Sei amministratore, quindi puoi correggerli tu stesso — anche i tuoi —
                    da <Link to="/impostazioni/utenti">Impostazioni → Utenti</Link>. Sono due
                    passaggi invece di uno di proposito: cambiare un nome lì è un'operazione
                    che resta scritta nel registro.
                  </>
                ) : (
                  <>
                    Se c'è un errore da correggere — un cognome sbagliato, un indirizzo
                    aziendale cambiato — chiedilo a un amministratore dello scadenziario:
                    lo sistema in un momento da Impostazioni → Utenti.
                  </>
                )}
              </p>
            </div>
          </section>

          <section className="pannello pannello-imbottito" aria-labelledby="profilo-avvisi">
            <h2 className="titolo-riquadro" id="profilo-avvisi">Avvisi via email</h2>
            <p className="aiuto" style={{ marginTop: 0, marginBottom: 12, maxWidth: '62ch' }}>
              Questa è tua e la decidi tu: riguarda la tua casella di posta, non il lavoro
              degli altri. Spegnerla non cambia niente per i colleghi, che continuano a
              ricevere i propri avvisi.
            </p>

            {/* Lo stesso componente della voce nel menu del bollino: quello che
                succede quando il salvataggio fallisce è scritto una volta sola. */}
            <InterruttoreNotifiche utente={utente} ricarica={ricarica} variante="pagina" />
          </section>
        </div>

        <RiquadroPassword />
      </div>
    </>
  );
}

/**
 * Il cambio password, con il preavviso attaccato al pulsante.
 *
 * L'avvertimento sta **sopra** il pulsante e non sotto, e non è scritto come
 * una nota di aiuto: è la conseguenza di ciò che si sta per premere, non un
 * dettaglio. Chi legge da tastiera lo incontra prima di arrivare al pulsante.
 */
function RiquadroPassword() {
  const { esci } = useAutenticazione();

  const [attuale, setAttuale] = useState('');
  const [nuova, setNuova] = useState('');
  const [conferma, setConferma] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [fatto, setFatto] = useState(false);

  async function invia() {
    if (nuova !== conferma) {
      setErrore('Le due password non coincidono.');
      return;
    }

    if (nuova === attuale) {
      setErrore('La nuova password è identica a quella attuale: così non cambia niente.');
      return;
    }

    setErrore(null);
    setInCorso(true);

    try {
      await autenticazione.cambiaPassword(attuale, nuova);

      /*
       * Il messaggio si lascia **prima** di mostrare la conferma, non dopo.
       *
       * Da questo istante la sessione è morta: la prima chiamata qualsiasi
       * riceve un 401 e il contesto porta alla schermata di accesso senza
       * chiedere il permesso — può succedere anche mentre si sta leggendo il
       * riquadro verde qui sotto, se nel frattempo si torna sulla finestra.
       * Se il motivo non fosse già stato depositato, quell'atterraggio
       * sarebbe muto.
       */
      annunciaCongedo(
        'Hai cambiato la password: tutte le sessioni sono state chiuse, compresa quella da cui l\'hai cambiata. Rientra con la password nuova.',
      );

      setFatto(true);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  if (fatto) {
    return (
      <section className="pannello pannello-imbottito" aria-labelledby="profilo-password">
        <h2 className="titolo-riquadro" id="profilo-password">Password cambiata</h2>

        <div className="avviso-esito" role="status" style={{ display: 'block' }}>
          Da adesso vale solo quella nuova, e la conosci solo tu.
        </div>

        <p className="aiuto" style={{ maxWidth: '62ch' }}>
          Come anticipato, tutte le sessioni aperte sono state chiuse — anche questa, in cui
          stai leggendo. L'applicazione non è caduta: sei stato scollegato apposta. Rientra
          con la password nuova.
        </p>

        <div className="form-azioni">
          <button type="button" className="bottone bottone-principale" onClick={() => void esci()}>
            Vai all'accesso
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="pannello pannello-imbottito" aria-labelledby="profilo-password">
      <h2 className="titolo-riquadro" id="profilo-password">Cambia la tua password</h2>

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <form onSubmit={(e) => { e.preventDefault(); void invia(); }}>
        <div className="campo">
          <label htmlFor="pr-attuale">Password attuale</label>
          <input
            id="pr-attuale"
            type="password"
            value={attuale}
            onChange={(e) => setAttuale(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <div className="campo">
          <label htmlFor="pr-nuova">Nuova password</label>
          <input
            id="pr-nuova"
            type="password"
            value={nuova}
            onChange={(e) => setNuova(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
          />
          <p className="aiuto">
            Almeno 12 caratteri. Nessun obbligo di maiuscole o simboli: una frase lunga
            che ricordi è più sicura di «Password1!» e non finisce su un post-it.
          </p>
        </div>

        <div className="campo">
          <label htmlFor="pr-conferma">Ripeti la nuova password</label>
          <input
            id="pr-conferma"
            type="password"
            value={conferma}
            onChange={(e) => setConferma(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {/* Il preavviso, prima del pulsante. Non è una nota: è cosa succede
            premendolo, e chi lo scopre dopo pensa che sia andato storto
            qualcosa.

            Lo stacco dal campo qui sopra è voluto e più largo del solito:
            attaccato, si legge come il testo d'aiuto di quel campo — cioè come
            una regola su come ripetere la password — invece che come
            l'avvertimento su cosa sta per succedere. */}
        <div className="avviso-attenzione" style={{ marginTop: 24 }}>
          <p style={{ margin: 0 }}>
            <strong>Salvando esci da qui.</strong> Cambiare password chiude tutte le
            sessioni aperte, anche questa: finito il salvataggio torni alla schermata di
            accesso e devi rientrare con la password nuova.
          </p>
          <p className="aiuto" style={{ marginBottom: 0 }}>
            È voluto. Se stai cambiando password perché temi che qualcuno l'abbia scoperta,
            lasciare viva una sessione aperta altrove vanificherebbe tutto il resto.
          </p>
        </div>

        <div className="form-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : (
              <>
                <IconaChiave /> Cambia password ed esci
              </>
            )}
          </button>
        </div>
      </form>
    </section>
  );
}

function IconaChiave() {
  return (
    <Icona lato={15}>
      <circle cx="8" cy="15.4" r="3.6" />
      <path d="m10.7 13 7.8-7.8" />
      <path d="m16.4 7.3 2 2" />
      <path d="m14.4 9.3 2 2" />
    </Icona>
  );
}
